<template>
  <div>
    <v-progress-circular
      v-if="progressValue < 100"
      class="overlayProgressBar"
      :rotate="-90"
      :size="100"
      :width="15"
      :value="progressValue"
      color="primary"
    >
      {{ progressValue }}%
    </v-progress-circular>

    <div v-if="!isLoading" class="pa-0 ma-0">
      <v-expansion-panels
        v-model="openedFilterPanel"
        default
        :class="{
          datatableLoading: isLoadingRemainingData,
          'py-2': true,
          'px-1': true,
        }"
      >
        <v-expansion-panel>
          <v-expansion-panel-header
            class="text-h6"
            style="background-color: #e0e0e9;"
          >
            <v-tooltip top color="primary" class="pl-4">
              <template v-slot:activator="{ on: tooltip }">
                <div v-on="{ ...tooltip }">
                  Filter/search variables

                  <v-chip
                    label
                    :color="
                      getVisibleItemCount() < variables.length
                        ? colors['population']
                        : 'primary'
                    "
                    :text-color="
                      getVisibleItemCount() < variables.length
                        ? 'black'
                        : 'white'
                    "
                    class="title ml-3"
                  >
                    {{ getVisibleItemCount() }} /
                    {{ variables.length }} variables shown
                  </v-chip>

                  <v-chip
                    label
                    :color="nFiltersEnabled > 0 ? 'red' : 'primary'"
                    text-color="white"
                    class="title ml-3"
                  >
                    {{ nFiltersEnabled }} / 3 filters enabled
                  </v-chip>

                  <v-spacer></v-spacer>
                </div>
              </template>
              <span class="subtitle-1">
                {{
                  openedFilterPanel == 0
                    ? 'click to hide variable filters'
                    : 'click to view and adjust the variable filters'
                }}
              </span>
            </v-tooltip>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pl-3">
            <v-container class="pa-0 ma-0 pt-2">
              <v-row class="pa-0 ma-0">
                <v-col cols="10" class="pa-0 ma-0 pt-2">
                  <v-slider
                    v-model="min_var_subjects"
                    class="pa-0 pt-3"
                    label="Only show variables with at least N total subjects"
                    step="1"
                    thumb-label="always"
                    min="0"
                    :max="max_var_subject_total"
                  >
                  </v-slider>
                </v-col>

                <v-col cols="2" class="pa-0 ma-0 pl-2 pt-1">
                  <v-text-field
                    v-model.number="min_var_subjects"
                    class="center-text pa-0 ma-0"
                    type="number"
                    outlined
                  ></v-text-field>
                  <!--
                  ({{
                    Math.round(
                      (min_var_subjects /
                        (max_var_subject_total == 0
                          ? 1
                          : max_var_subject_total)) *
                        100
                    )
                  }}%)
-->
                </v-col>
              </v-row>

              <v-row class="pa-0 ma-0">
                <v-col cols="10" class="pa-0 ma-0">
                  <v-text-field
                    v-model="var_search_text"
                    class="center-text pa-0 ma-0"
                    label="Show variables with a domain or name that matches this text:"
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="pa-0 ma-0">
                <v-col cols="10" class="pa-0 ma-0">
                  <v-checkbox
                    v-model="show_selected_vars_only"
                    label="Only show selected variables"
                  ></v-checkbox>
                </v-col>
                <v-col cols="2">
                  <v-btn
                    :disabled="nFiltersEnabled == 0"
                    class="primary text--white ma-0 px-2 py-0"
                    @click="clearAllFilters"
                    >Reset all filters</v-btn
                  >
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-data-table
        ref="var_table"
        v-model="selected"
        class="ml-1 mr-1"
        :class="{ datatableLoading: isLoadingRemainingData }"
        :items="variables"
        :custom-sort="customSort"
        :custom-filter="customFilter"
        filter-mode="intersection"
        item-key="id"
        :headers="headers"
        disable-pagination
        show-select
        hide-default-footer
        :search="var_search_text"
        @current-items="updateTableItems"
      >
        <!-- Add "All:" to select all checkbox -->
        <template
          v-if="!isLoadingRemainingData"
          v-slot:header.data-table-select="{ on, props }"
        >
          <v-tooltip v-if="!useAutomatedAnalysisMode" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span
                class="text-subtitle-1 font-weight-bold"
                v-on="{ ...tooltip }"
              >
                All:
                <v-checkbox
                  v-model="selectAll"
                  dense
                  hide-details
                  class="pa-0 ma-0"
                  @change="selectAllCheckboxChange($event)"
              /></span>
            </template>
            <span class="subtitle-1">Click to select all variables.</span>
          </v-tooltip>

          <v-tooltip v-else top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span
                class="text-subtitle-1 font-weight-bold"
                v-on="{ ...tooltip }"
              >
                Predictors
              </span>
            </template>
            <span class="subtitle-1"
              >Select all variables to use as predictors in the automated
              analysis.</span
            >
          </v-tooltip>
        </template>

        <template v-slot:header.scale="{ header }">
          Variable<br />
          <v-tooltip top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-chip
                :color="getNumVariablesColor(n_selected_observation_vars)"
                :text-color="
                  getNumVariablesTextColor(n_selected_observation_vars)
                "
                class="title ma-2 ml-0"
                v-on="{ ...tooltip }"
                >{{
                  n_selected_subject_vars +
                    n_selected_observation_vars +
                    '/' +
                    variables.length +
                    ' selected'
                }}</v-chip
              >
            </template>
            <span class="subtitle-1"
              >{{ n_selected_subject_vars }} demographic/subject variable<span
                v-if="n_selected_subject_vars != 1"
                >s</span
              >
              and {{ n_selected_observation_vars }} outcome variable<span
                v-if="n_selected_observation_vars != 1"
                >s</span
              >
              selected</span
            >
            <span v-if="n_selected_observation_vars == 0"
              ><br /><v-icon class="pa-2 pl-0" color="warning" large
                >warning</v-icon
              >At least one outcome (i.e., non-demographic) variable must be
              selected to create a valid study dataset.</span
            >
          </v-tooltip>
        </template>

        <template
          v-for="(ds, index) in datasets"
          v-slot:[`header.${ds.study_name}`]="{ header }"
        >
          {{ header.dataset.study_name }} <br :key="`dsvc-br-${index}`" />
          <v-tooltip :key="`dsvc-tt-${index}`" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-chip
                :key="`dsvc-${index}`"
                :color="getNumSubjectsColor(getStudyCount(header.dataset.id))"
                :text-color="
                  getNumSubjectsTextColor(getStudyCount(header.dataset.id))
                "
                class="title ma-2"
                v-on="{ ...tooltip }"
                >{{ getStudyCount(header.dataset.id) }} selected</v-chip
              >
            </template>
            <span class="subtitle-1"
              >{{ getStudyCount(header.dataset.id) }} subject(s) selected</span
            >
          </v-tooltip>
        </template>

        <template v-if="!isLoadingRemainingData" v-slot:item="props">
          <tr>
            <td v-if="!useAutomatedAnalysisMode">
              <v-checkbox
                :key="'cb-' + props.item.scale + '-' + props.item.id"
                v-model="props.item.isSelected"
                :true-value="true"
                :false-value="false"
                @change="checkboxChange(props, $event)"
              ></v-checkbox>
            </td>
            <td v-if="useAutomatedAnalysisMode">
              <v-checkbox
                :key="'cb-in-' + props.item.scale + '-' + props.item.id"
                v-model="props.item.isSelectedInput"
                @change="inputCheckboxChange(props, $event)"
              ></v-checkbox>
            </td>
            <td v-if="useAutomatedAnalysisMode">
              <!-- longitudinal outcome variable -->
              <v-select
                v-if="
                  isLongitudinal &&
                    props.item.isSelectedInput &&
                    props.item.type == 'observation'
                "
                v-model="props.item.aaWhichOutcome"
                :items="getOutcomeVarVisitChoices(props.item)"
                item-text="name"
                item-value="id"
                label="value to use"
                class="pa-0 ma-0 pt-3"
                dense
              ></v-select>

              <!-- continuous subject variable -->
              <v-select
                v-if="
                  props.item.isSelectedInput &&
                    props.item.data_category == 'Continuous' &&
                    props.item.value_type != 'date'
                "
                v-model="props.item.aaRanges"
                :items="['quartiles', 'tertiles', 'halves']"
                label="compare"
                class="pa-0 ma-0 pt-3"
                dense
              ></v-select>
              <!-- categorical subject variable -->
              <!-- changes to minimum category size list must be coordinated with SaveCollectionBtnDialog.vue -->
              <v-select
                v-if="
                  props.item.isSelectedInput &&
                    (props.item.data_category == 'Categorical' ||
                      props.item.data_category == 'Ordinal')
                "
                v-model="props.item.aaMinCatSize"
                :items="[1, 5, 10, 25, 50]"
                label="minimum category size"
                class="pa-0 ma-0 pt-3"
                dense
              ></v-select>
            </td>
            <td v-if="useAutomatedAnalysisMode">
              <v-checkbox
                :key="'cb-out-' + props.item.scale + '-' + props.item.id"
                v-model="props.item.isSelectedOutput"
                @change="outputCheckboxChange(props, $event)"
              ></v-checkbox>
            </td>
            <td class="subtitle-1 text-xs-left">
              <v-row align="center" class="pa-0 ma-0">
                <span style="padding:0.5em 0.5em 0em 0em"
                  ><img
                    :src="'/images/' + props.item.category + '-icon-128.png'"
                    :title="props.item.category"
                    style="height:2.5em"
                    @error="missingDomainFallback"
                /></span>
                {{ props.item.category }}
              </v-row>
            </td>
            <td class="subtitle-1 text-xs-left">
              <v-tooltip top color="primary">
                <template v-slot:activator="{ on: tooltip }">
                  <span v-on="{ ...tooltip }">
                    {{
                      useLongScaleNames
                        ? props.item.scale
                        : props.item.abbreviation
                    }}
                  </span>
                </template>
                <span
                  v-if="props.item.scale != props.item.abbreviation"
                  class="subtitle-1"
                >
                  {{ props.item.abbreviation }} <br clear="both" />
                </span>
                <span class="subtitle-1"> {{ props.item.scale }} </span>
                <span
                  v-if="props.item.scale != props.item.description"
                  class="subtitle-1"
                >
                  <br clear="both" />
                  {{ props.item.description }}
                </span>
              </v-tooltip>
            </td>

            <td
              v-for="dataset in datasets"
              :key="dataset.id"
              class="subtitle-1 text-xs-center"
            >
              <v-container class="pa-0 ma-0">
                <v-row
                  justify="center"
                  class="pa-0 ma-0"
                  style="cursor:pointer"
                  @click="onGraphClick(dataset, props.item)"
                >
                  <histogram-sparkline
                    :key="'sl-' + dataset.id + '-' + props.item.id"
                    :dataset-id="dataset.id"
                    :type="props.item.type"
                    :scale-id="props.item.id"
                    :data-category="props.item.data_category"
                  />
                </v-row>
                <v-row justify="center" class="pa-0 ma-0">
                  <span
                    :style="
                      'color: ' +
                        getStudyVariableColor(
                          getStudyVariableCount(dataset.id, props.item)
                        )
                    "
                  >
                    {{ getStudyVariableCount(dataset.id, props.item) }}</span
                  >
                </v-row></v-container
              >
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <!-- group select dialog -->
    <v-dialog v-model="groupSelectDialog" width="500" persistent>
      <v-card>
        <v-card-title color="white" primary-title>
          <span class="primary--text text--darken-3 title pl-2"
            >{{ groupVarSelected.scale }} selected</span
          >
        </v-card-title>
        <v-card-text class="primary primary--text text--lighten-5 pt-4">
          Do you wish to select all the variables in the same group as
          {{ groupVarSelected.scale }} (see list below)?
          <ul v-if="groupSelected != null" class="pt-2">
            <li
              v-for="(v, vi) in groupSelected.vars"
              :key="'lv-' + groupVarSelected.scale + '-' + vi"
            >
              {{ v.var.scale }}
            </li>
          </ul>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="groupSelectDialog = false"
            ><v-icon left>close</v-icon>No</v-btn
          >
          <v-btn
            color="primary"
            @click="
              selectVarGroup(groupSelected, true);
              groupSelectDialog = false;
            "
            >Yes - Select All</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="variableSummaryDialog" width="50%">
      <variable-summary-chart
        :variable="clickedVar"
        :dataset="clickedDataset"
        :use-long-scale-names="useLongScaleNames"
        :show-dataset-name="datasets.length > 1"
        @close="variableSummaryDialog = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import HistogramSparkline from '@/components/DatasetManager/HistogramSparkline.vue';
import VariableSummaryChart from '@/components/DatasetManager/VariableSummaryChart.vue';
import {
  colors,
  getNumSubjectsColor,
  getNumSubjectsTextColor,
  getNumVariablesColor,
  getNumVariablesTextColor,
} from '@/utils/colors';
import {
  strCompare,
  numericSuffixStrCompare,
  sortScales,
  countMaxSubjects,
  estimateMaxSubjects,
  getVariableGroups,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    HistogramSparkline,
    VariableSummaryChart,
  },
  props: {
    datasets: {
      type: Array,
      default: () => [],
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    useAutomatedAnalysisMode: {
      type: Boolean,
      default: false,
    },
    useLongScaleNames: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: true,
      isLoadingRemainingData: true,
      selectAll: false,
      // v-data-table v-model - not used
      selected: [],
      selectedVars: [],
      // variables currently displayed by the table
      currentVars: [],
      variables: [],
      headers: [],
      subject_variables: {},
      subject_variable_visits: {},
      study_variable_counts: { subject: {}, observation: {} },
      subject_counts: { all: 0 },
      min_var_subjects: 0,
      var_subject_totals: {},
      max_var_subject_total: 0,
      show_selected_vars_only: false,
      colors: colors,
      // auto-analysis
      auto_analysis_inputs: {},
      auto_analysis_inputs_list: [],
      auto_analysis_outputs: {},
      auto_analysis_outputs_list: [],
      // selected variable counts
      n_selected_subject_vars: 0,
      n_selected_observation_vars: 0,
      progressValue: 0,
      progressValueCap: 10,
      openedFilterPanel: null,
      single_var_counts: {},
      var_search_text: '',
      variableGroups: [],
      groupVarSelected: { scale: 'N/A' },
      groupSelected: null,
      groupSelectDialog: false,
      groupsSelected: {},
      variableSummaryDialog: false,
      clickedVar: null,
      clickedDataset: null,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    isLongitudinal() {
      var is_longitudinal = true;
      this.datasets.forEach(d => {
        if (!d.longitudinal) {
          is_longitudinal = false;
        }
      });
      return is_longitudinal;
    },
    nFiltersEnabled() {
      let nf = 0;
      if (this.min_var_subjects > 0) {
        nf += 1;
      }
      if (this.var_search_text != '') {
        nf += 1;
      }
      if (this.show_selected_vars_only) {
        nf += 1;
      }
      return nf;
    },
  },
  watch: {
    useAutomatedAnalysisMode(aam) {
      // enter automated mode
      if (aam) {
        // assign variable to predictor/outcome based on type (demographic/non-demographic)
        let selVars = {};
        this.selectedVars.forEach(sv => {
          selVars[sv.id] = true;
        });

        this.variables.forEach(v => {
          if (v.id in selVars && !v.isSelectedInput && !v.isSelectedOutput) {
            if (v.type == 'subject' || v.category == 'Demographics') {
              v.isSelectedInput = true;
              this.updateInputVar(v);
            } else {
              v.isSelectedOutput = true;
              this.updateOutputVar(v);
            }
          }
          v.isSelected = false;
        });
      }
      // leave automated mode
      else {
        // merge input/output variables
        this.variables.forEach(v => {
          v.isSelected = v.isSelectedInput || v.isSelectedOutput;
        });
      }
      this.headers = this.getTableHeaders();
    },
    auto_analysis_inputs_list(ipl) {
      this.$emit('autoAnalysisInputs', ipl);
    },
    auto_analysis_outputs_list(opl) {
      this.$emit('autoAnalysisOutputs', opl);
    },
    collection() {
      this.selectCollectionVars();
    },
    datasets() {
      this.headers = this.getTableHeaders();
    },
  },
  mounted() {
    this.intervalId = setInterval(() => {
      //check if progressValue reached progressValueCap
      if (this.progressValue < this.progressValueCap) {
        this.progressValue += 1;
      }
    }, 250);
    this.groupsSelected = {};
  },
  beforeDestroy() {
    // Clear the interval when the component is destroyed to prevent memory leaks
    clearInterval(this.intervalId);
  },
  async created() {
    this.headers = this.getTableHeaders();
    const {
      data: { variables },
    } = await this.fetchSharedVariables();

    this.progressValue = 10;
    this.progressValueCap = 29;
    // add type key to variables to distinguish that these variables
    // are relating to the observations
    variables.forEach(variable => {
      variable.type = 'observation';
    });
    variables.forEach(x => {
      x.full_path = x.category + '/' + x.scale;
    });
    this.variables = sortScales(variables.filter(v => v.value_type !== 'date'));
    this.variables.forEach(v => {
      v.isSelected = false;
    });
    this.progressValue = 30;
    this.progressValueCap = 79;
    this.isLoading = false;

    const { data: subjVarVisits } = await this.fetchSubjectVariableVisits();
    this.subject_variable_visits = subjVarVisits['visits'];
    this.progressValue = 80;
    this.progressValueCap = 89;
    // build hash that maps variable_id + study_id -> number of subjects
    this.computeStudyVariableCounts();

    // fetch shared subject attributes
    var {
      data: { attributes },
    } = await this.fetchSharedAttributes();

    this.progressValue = 90;
    this.progressValueCap = 99;
    // add type key to subject variables to distinguish that these variables
    // are relating to the subjects
    attributes.forEach(variable => {
      variable.type = 'subject';
      variable.full_path = variable.category + '/' + variable.scale;
    });
    attributes = attributes.filter(a => a.value_type !== 'date');

    this.variables = sortScales([...this.variables, ...attributes]);
    this.variables.forEach(v => {
      v.isSelected = false;
    });
    this.isLoadingRemainingData = false;
    this.progressValue = 99;
    //wait 100ms to allow progress bar to reach 100%
    setTimeout(() => {
      this.progressValue = 100;
    }, 100);
    // when used to update an existing collection:
    this.selectCollectionVars();
    this.updateSingleVarCounts();
    this.updateVariableGroups();
  },
  methods: {
    getNumSubjectsColor,
    getNumSubjectsTextColor,
    getNumVariablesColor,
    getNumVariablesTextColor,

    updateSelectedVariables() {
      let selVars = [];
      this.variables.forEach(v => {
        if (v.isSelected || v.isSelectedInput || v.isSelectedOutput) {
          selVars.push(v);
        }
      });

      // track number of selected subject and observation_variables
      var n_subject = 0;
      var n_observation = 0;

      selVars.forEach(v => {
        if (v.type === 'subject') {
          n_subject += 1;
        } else if (v.type === 'observation') {
          n_observation += 1;
        }
      });

      // TODO - check visit_num also and return the higher of the two
      var ems = estimateMaxSubjects(
        this.subject_variable_visits,
        selVars,
        'event'
      );
      this.subject_counts = ems['counts'];
      this.selectedVars = selVars;
      if (this.selectable) this.$emit('input', selVars);
      this.$emit('nSubjects', this.subject_counts['all']);
      this.$emit('nSubjectVars', n_subject);
      this.$emit('nObservationVars', n_observation);
      this.n_selected_subject_vars = n_subject;
      this.n_selected_observation_vars = n_observation;
      this.selected = [];
    },
    updateTableItems(ni) {
      this.currentVars = ni;
    },
    /**
     * If dataset id is an array of ids, we want to
     * call the API endpoint that gets their intersecting
     * variables.
     */
    fetchSharedVariables() {
      const base = `/api/studies/variables`;
      const query = this.datasets.map(({ id }) => `id=${id}`).join('&');
      return axios.get(`${base}?${query}`);
    },
    fetchSharedAttributes() {
      const base = `/api/studies/attributes`;
      const query = this.datasets.map(({ id }) => `id=${id}`).join('&');
      return axios.get(`${base}?${query}`);
    },
    /**
     * Retrieve list of subjects along with the variables measured (first + last) for each.
     */
    fetchSubjectVariables() {
      const base = `/api/studies/subject_variables`;
      const query = this.datasets.map(({ id }) => `id=${id}`).join('&');
      return axios.get(`${base}?${query}`);
    },
    /**
     * Retrieve list of subjects along with the variables measured at each visit.
     */
    fetchSubjectVariableVisits() {
      const base = `/api/studies/subject_variable_visits`;
      const query = this.datasets.map(({ id }) => `id=${id}`).join('&');
      return axios.get(`${base}?${query}`);
    },
    /**
     * Build hash that maps variable_id + study_id -> number of subjects
     */
    computeStudyVariableCounts() {
      var svc = { subject: {}, observation: {} };
      var svars = this.subject_variable_visits['subjects'];
      var nvisits = this.isLongitudinal ? 2 : 1;

      const subj_ids = Object.keys(svars);
      subj_ids.forEach(subj_id => {
        var study_ids = Object.keys(svars[subj_id]);
        study_ids.forEach(study_id => {
          var var_types = Object.keys(svars[subj_id][study_id]);
          var_types.forEach(var_type => {
            var var_ids = Object.keys(svars[subj_id][study_id][var_type]);
            var_ids.forEach(var_id => {
              var key = var_id + ':' + study_id;
              var add_one = true;

              // check for the expected number of measurements
              if (
                typeof svars[subj_id][study_id][var_type][var_id] != 'number'
              ) {
                var evtStr =
                  svars[subj_id][study_id][var_type][var_id]['event'];
                var nEvt = evtStr.split('1').length - 1;
                var numStr = svars[subj_id][study_id][var_type][var_id]['num'];
                var nNum = numStr.split('1').length - 1;
                if (nEvt < nvisits && nNum < nvisits) add_one = false;
              }
              if (add_one) {
                if (!(key in svc[var_type])) {
                  svc[var_type][key] = 0;
                }
                svc[var_type][key] = svc[var_type][key] + 1;
              }
            });
          });
        });
      });
      this.study_variable_counts = svc;
    },

    getStudyVariableCount(study_id, v) {
      let var_id = v.id;
      let var_type = v['type'] == 'subject' ? 'subject' : 'observation';
      return this.single_var_counts[var_type][var_id][study_id];
    },

    getStudyVariableColor(nSubjects) {
      if (nSubjects <= 25) {
        return '#F83008';
      } else {
        return 'black';
      }
    },

    getStudyCount(study_id) {
      if (study_id in this.subject_counts) {
        return this.subject_counts[study_id];
      } else {
        return 0;
      }
    },

    /**
     * Count the number of subjects in the Dataset for a given list of variable ids.
     */
    countSubjects(vars) {
      var subjCounts = { all: 0 };
      if (vars.length === 0) return { counts: subjCounts };
      this.datasets.forEach(d => {
        subjCounts[d.id] = 0;
      });
      const subj_vars = this.subject_variables['subjects'];
      const subj_ids = Object.keys(subj_vars);
      subj_ids.forEach(subj_id => {
        var include_subject = true;
        var study_ids = Object.keys(subj_vars[subj_id]);
        study_ids.forEach(study_id => {
          const svars = subj_vars[subj_id][study_id];

          // check for presence of all requested vars:
          vars.forEach(v => {
            if (!(v.id in svars[v.type])) {
              include_subject = false;
            }
          });
          if (include_subject) {
            subjCounts[study_id] += 1;
            subjCounts['all'] += 1;
          }
        });
      });
      return { counts: subjCounts };
    },
    // add or remove variables in a group to/from selected variables
    selectVarGroup(g, sel) {
      let sel_scales = {};
      // TODO - handle case where automated analysis mode is enabled
      g['vars'].forEach(v => {
        sel_scales[v.var.scale] = true;
      });
      this.variables.forEach(v => {
        if (v.scale in sel_scales) v.isSelected = sel;
      });
      this.updateSelectedVariables();
    },

    checkboxChange(props, evt) {
      props.item.isSelected = evt;
      let group = this.variableGroups['var2group'][props.item.scale];
      // group (de)select
      if (group != null && group.vars.length > 1) {
        // case 1: autoselect
        if (group.min_index == 1) {
          this.selectVarGroup(group, evt);
          return;
        }
        // case 2: ask user to confirm group select, but only once per group
        else if (evt == true && !(group.id in this.groupsSelected)) {
          props.select(evt);
          this.groupVarSelected = props.item;
          this.groupSelected = group;
          this.groupSelectDialog = true;
          this.groupsSelected[group.id] = 1;
          return;
        }
      }
      // single (de)select
      this.updateSelectedVariables();
    },
    selectAllCheckboxChange(sel) {
      let sel_scales = {};
      this.currentVars.forEach(v => {
        sel_scales[v.scale] = true;
      });
      // select all checkbox does not appear in automated analysis mode
      this.variables.forEach(v => {
        if (v.scale in sel_scales) v.isSelected = sel;
      });
      this.updateSelectedVariables();
    },
    updateInputVar(v) {
      var sel = v.isSelectedInput;
      if (sel) {
        this.auto_analysis_inputs[v.id] = v;
        if (v.data_category == 'Continuous' && v.value_type != 'date') {
          v.aaRanges = 'halves';
        } else if (
          v.data_category == 'Categorical' ||
          v.data_category == 'Ordinal'
        ) {
          v.aaMinCatSize = 25;
        }
        if (this.isLongitudinal && v.type == 'observation') {
          v.aaWhichOutcome = 'firstVisit';
        }
      } else {
        delete this.auto_analysis_inputs[v.id];
      }
      this.auto_analysis_inputs_list = Object.values(this.auto_analysis_inputs);
      this.auto_analysis_outputs_list = Object.values(
        this.auto_analysis_outputs
      );
    },
    inputCheckboxChange(props, evt) {
      props.item.isSelected = false;
      props.item.isSelectedInput = evt;
      this.updateInputVar(props.item);
      this.updateSelectedVariables();
    },
    updateOutputVar(v) {
      var sel = v.isSelectedOutput;
      if (sel) {
        this.auto_analysis_outputs[v.id] = v;
      } else {
        delete this.auto_analysis_outputs[v.id];
      }
      this.auto_analysis_inputs_list = Object.values(this.auto_analysis_inputs);
      this.auto_analysis_outputs_list = Object.values(
        this.auto_analysis_outputs
      );
    },
    outputCheckboxChange(props, evt) {
      props.item.isSelected = false;
      props.item.isSelectedOutput = evt;
      this.updateOutputVar(props.item);
      this.updateSelectedVariables();
    },
    getOutcomeVarVisitChoices(v) {
      let choices = [
        { id: 'firstVisit', name: 'First Visit' },
        { id: 'lastVisit', name: 'Last Visit' },
      ];
      if (v.data_category == 'Continuous') {
        choices.push({ id: 'change', name: 'Change' });
        choices.push({ id: 'roc', name: 'Rate of Change' });
      }
      return choices;
    },
    selectCollectionVars() {
      if (this.collection == null) return;
      let subj_vars = this.collection.subject_variables;
      let subj_ids = {};
      subj_vars.forEach(sv => {
        subj_ids[sv.ontology.id] = 1;
      });

      let obs_vars = this.collection.observation_variables;
      let obs_ids = {};
      obs_vars.forEach(sv => {
        obs_ids[sv.ontology.id] = 1;
      });

      this.variables.forEach(v => {
        // observation variable
        if ('flip_axis' in v) {
          if (v.id in obs_ids) {
            v.isSelected = true;
          }
        }
        // subject variable
        else if (v.id in subj_ids) {
          v.isSelected = true;
        }
      });
      this.updateSelectedVariables();
    },
    getTableHeaders() {
      var hdrs = [];
      if (this.useAutomatedAnalysisMode) {
        hdrs.push({
          text: 'Study Group Parameters',
          value: '',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
        });
        hdrs.push({
          text: 'Outcomes',
          value: 'output',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
        });
      }
      hdrs.push({
        text: 'Domain',
        value: 'category',
        sortable: true,
        class: 'text-subtitle-1 font-weight-bold',
      });
      hdrs.push({
        text: 'Variable',
        value: 'scale',
        sortable: true,
        class: 'text-subtitle-1 font-weight-bold',
        filter: (value, search, item) => {
          let minSubjMatch =
            !(item.id in this.var_subject_totals) ||
            this.var_subject_totals[item.id] >= this.min_var_subjects;
          let selectedMatch =
            !this.show_selected_vars_only ||
            (item.isSelected && !this.useAutomatedAnalysisMode) ||
            (this.useAutomatedAnalysisMode &&
              (item.isSelectedInput || item.isSelectedOutput));
          return minSubjMatch && selectedMatch;
        },
      });

      // append the dataset study names as headers so
      // we can see study variable distributions as
      // columns
      this.datasets.map(dataset => {
        hdrs.push({
          dataset: dataset,
          text: dataset.study_name,
          value: dataset.study_name,
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
          align: 'center',
        });
      });
      return hdrs;
    },
    updateSingleVarCounts() {
      // compute max subject number for each variable and dataset independently
      let svc = { subject: {}, observation: {} };
      // total subjects per variable, summed across all datasets
      let var_subject_totals = {};
      // maximum total subjects per variable, summed across all datasets
      let max_var_subject_total = 0;
      this.variables.forEach(v => {
        let v_type = v['type'] == 'subject' ? 'subject' : 'observation';
        let selVar = { id: v.id, type: v_type, label: v.abbreviation };

        // TODO - check visit_num also and return the higher of the two
        let ms = countMaxSubjects(
          this.subject_variable_visits,
          selVar,
          'event'
        );

        if (!(v.id in svc[v_type])) svc[v_type][v.id] = {};
        let var_subject_total = 0;
        Object.keys(ms['counts']).forEach(k => {
          if (k != 'all') {
            let ct = ms['counts'][k];
            var_subject_total += ct;
            svc[v_type][v.id][k] = ct;
          }
        });
        var_subject_totals[v.id] = var_subject_total;
        if (var_subject_total > max_var_subject_total) {
          max_var_subject_total = var_subject_total;
        }
      });
      this.single_var_counts = svc;
      this.var_subject_totals = var_subject_totals;
      this.max_var_subject_total = max_var_subject_total;
    },
    customSort(items, index, isDesc) {
      let cs_fn = function(a, b) {
        return index[0] === 'scale'
          ? numericSuffixStrCompare(a.scale, b.scale)
          : strCompare(a[index], b[index]);
      };
      let ss = index == '' ? sortScales(items) : items.sort(cs_fn);
      return isDesc == 'true' ? ss.reverse() : ss;
    },
    customFilter(value, search, item) {
      const lc_search = search == null ? null : search.toLowerCase();
      return (
        lc_search == null ||
        item.category.toLowerCase().indexOf(lc_search) !== -1 ||
        item.abbreviation.toLowerCase().indexOf(lc_search) !== -1 ||
        item.scale.toLowerCase().indexOf(lc_search) !== -1
      );
    },
    updateVariableGroups() {
      this.variableGroups = getVariableGroups(this.variables);
    },
    getVisibleItemCount() {
      if (!('var_table' in this.$refs)) return 0;
      if (!('internalCurrentItems' in this.$refs.var_table)) return 0;
      return this.$refs.var_table.internalCurrentItems.length;
    },
    clearAllFilters() {
      this.min_var_subjects = 0;
      this.var_search_text = '';
      this.show_selected_vars_only = false;
    },
    onGraphClick(dataset, variable) {
      this.clickedVar = variable;
      this.clickedDataset = dataset;
      this.variableSummaryDialog = true;
    },
  },
};
</script>

<style scoped>
.overlayProgressBar {
  position: absolute;
  top: 20em;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
}
.datatableLoading {
  pointer-events: none;
  user-select: none;
  opacity: 0.4;
}
</style>
